import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';

import api from '../../api';
import { RawFileUploader } from './RawFileUploader';
import { Login } from './Login';
import { AssaySelector }  from './AssaySelector';
import { LipidClassSelector }  from './LipidClassSelector';
import { OptionalParameters, EasyOptionalParameters } from './OptionalParameters';
import { FattyAcidSetSelector } from './FattyAcidSetSelector';
import { DownloadResults } from './DownloadResults';

import { defaultOptionalParameters } from './defaultOptionalParameters';


export const SubmissionForm = ( {token, setToken, jobs, onSubmit} ) => {
  const [assays, setAssays] = useState(
    []
  );
  const [lipidClasses, setLipidClasses] = useState(
    []
  );
  const tolMax = 11;
  const [pFtol, setPFtol] = useState(6);
  const [pGtol, setPGtol] = useState(4);
  const [maxIter, setMaxIter] = useState(200);
  const [fattyAcids, setFattyAcids] = useState([]);

  const [selectedAssays, setSelectedAssays] = useState([]);
  const [selectedLipidClasses, setSelectedLipidClasses] = useState([]);

  const [mode, setMode] = useState("guess");

  const [toggleOptional, setToggleOptional] = useState(false);
  const [easy, setEasy] = useState(true);


  useEffect(() => {
    const fetchAssayList = async () => {
      if (token === null) {
        setAssays([]);
        setSelectedAssays([]);
        return null
      }
      const assaysResponse = await api.get(
        '/info/available-assays', {
          headers: {
            authorization: token
          },
        }
      );
      const selectedAssaysResponse = await api.get(
        '/submit/assays', {
          headers: {
            authorization: token
          },
        }
      );
      setAssays(assaysResponse.data.map(assay => ({value: assay, label: assay})));
      setSelectedAssays(selectedAssaysResponse.data.map(assay => ({value: assay.name, label: assay.name})));
    };
    const fetchLipidClassList = async () => {
      if (token === null) {
        setLipidClasses([])
        setSelectedLipidClasses([])
        return null
      }
      const lipidClassesResponse = await api.get(
        '/info/available-lipid-classes', {
          headers: {
            authorization: token
          },
        }
      );
      const selectedLipidClassesResponse = await api.get(
        '/submit/lipid-classes', {
          headers: {
            authorization: token
          },
        }
      );
      setLipidClasses(lipidClassesResponse.data.map((lipidClass, i) => ({
        value: i,
        label: lipidClass.shorthand,
        disabled: !lipidClass.available,
      })));
      setSelectedLipidClasses(selectedLipidClassesResponse.data.map((lipidClass, i) => ({
        value: i,
        label: lipidClass.shorthand,
        disabled: !lipidClass.available,
      })));
    };
    const fetchParameters = async () => {
      if (token === null) {
        setFattyAcids([]);
        return null
      }
      const response = await api.get(
        '/info/jobs/active', {
          headers: {
            authorization: token
          },
        }
      );
      // currently frontend only allows global settings
      // therefore the settings of job[0] is the same for the others as well!
      if (response.data.length > 0) {
        const job = response.data[0];
        setFattyAcids(job['start_profile'].map(e => e.fatty_acid));
        setPFtol(-Math.log10(job['ftol']));
        setPGtol(-Math.log10(job['gtol']));
        setMaxIter(job['max_iter']);
        setMode(job["mode"])
      }
      console.debug(mode)
    }
    fetchAssayList();
    fetchLipidClassList();
    setSelectedAssays(assays);
    setSelectedLipidClasses(lipidClasses);
    fetchParameters();
    setToken(token);
    console.log("Fetched information for submission form due to token change")
  }, [token]);

  async function handleFormSubmit(e) {
    e.preventDefault();

    const assayResponse = () => (
      api.post(
        "submit/assays",
        selectedAssays.map(a => a.value),
        {
          headers: {
            authorization: token
          }
        }
      )
    )
    const lipidClassResponse = () => (
      api.post(
        "submit/lipid-classes",
        selectedLipidClasses.map(lc => lc.label),
        {
          headers: {
            authorization: token
          }
        }
      )
    )
    await Promise.all(
      [assayResponse(), lipidClassResponse()]
    ).catch(errors => errors.forEach(
      c => {
        console.error(c)
        const message = c.response.data.detail;
        toast.error(message);
      }
    ));
    const fattyAcidResponse = () => (
      api.post(
        "submit/start-profiles",
        fattyAcids,
        {
          headers: {
            authorization: token
          },
          params: {
            mode: mode,
          }
        }
      )
    )
    const SettingsResponse = () => {
      return api.post(
        "submit/optimization-settings", {}, {
          headers: {
            authorization: token
          },
          params: {
            mode,
            ftol: pFtol === tolMax ? 0 : 10**-pFtol,
            gtol: pGtol === tolMax ? 0 : 10**-pGtol,
            max_iter: maxIter,
          },
        }
      )
    }
    await Promise.all(
      [fattyAcidResponse(), SettingsResponse()]
    ).catch(errors => errors.forEach(
      c => {
        console.error(c)
        const message = c.response.data.detail;
        toast.error(message);
      }
    ));

    await api.post(
      "/submit/optimizations",
      {},
      {
        headers: {
          authorization: token
        }
      }
    ).catch(error => {
      console.error(error);
      const message = error.response.data.detail;
      toast.error(message);
    })
    console.log("Setup jobs to process")
    updateJobs({token});
    console.log("Submitted jobs to process")
    console.debug(jobs)
  };

  const updateJobs = async ({token}) => {
    await api.get(
      "info/jobs/active",
      {
        headers: {
          authorization: token
        }
      }
    ).then(response => {onSubmit(response.data)})
  }


  return (
    <>
      <Tabs>
        <TabList>
          <Tab>Upload</Tab>
          <Tab>Login</Tab>
        </TabList>
        <TabPanel>
          <RawFileUploader setToken={setToken} />
        </TabPanel>
        <TabPanel>
          <Login setToken={setToken} />
        </TabPanel>
      </Tabs>
      <form onSubmit={handleFormSubmit}>
        <div className="mb-3">
          <div className="mb-3">
            <AssaySelector
              disabled={token === null}
              assays={assays}
              selectedAssays={selectedAssays}
              onChange={setSelectedAssays}
            />
            <LipidClassSelector
              disabled={token === null}
              lipidClasses={lipidClasses}
              selectedLipidClasses={selectedLipidClasses}
              onChange={setSelectedLipidClasses}
            />
          </div>
          <div className="mb-3">
            <FattyAcidSetSelector
              fattyAcids={fattyAcids}
              setFattyAcids={setFattyAcids}
            />
          </div>
          <div className="mb-3">
            <div className="card card-body">
              { easy ? <EasyOptionalParameters
                pFtol={pFtol}
                pGtol={pGtol}
                maxIter={maxIter}
                mode={mode}
                tolMax={tolMax}
                onChange={({pGtol, pFtol, maxIter, mode}) => {
                  setPGtol(pGtol);
                  setPFtol(pFtol);
                  setMaxIter(maxIter);
                  setMode(mode);
                  }}
                /> : <OptionalParameters
                  pFtol={pFtol}
                  setPFtol={setPFtol}
                  pGtol={pGtol}
                  setPGtol={setPGtol}
                  tolMax={tolMax}
                  maxIter={maxIter}
                  setMaxIter={setMaxIter}
                  mode={mode}
                  setMode={setMode}
                />
              }
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEasy(!easy)}>
                {easy ? "Advanced Mode" : "Easy Mode"}
              </button>
            </div>
          </div>
        </div>
        <div className="d-grid gap-1">
          <button
            type="submit"
            disabled={(token === null) || (selectedAssays.length === 0) || (selectedLipidClasses.length === 0) || (fattyAcids.length < 2)}
            className="btn btn-primary"
          >
            Submit Job
          </button>
          <DownloadResults
            token={token}
            jobs={jobs}
          />
        </div>
      </form>
    </>
  )
}
